<script setup lang="ts">
import {
  ComponentInternalInstance,
  computed,
  CSSProperties,
  nextTick,
  onMounted,
  onUnmounted,
  type PropType,
  provide,
  ref,
  watch,
} from "vue";
import { CssSize, TransitionEasing, WidthSize } from "@/models/types";

defineOptions({ name: "BcBurgerMenu" });

/** Specifies two-way binding (for visibility) for the burger menu */
const modelValue = defineModel<boolean | undefined>({ default: undefined });

const props = defineProps({
  closeEvents: {
    type: Array as PropType<Array<keyof DocumentEventMap>>,
    default: ["pointerdown"],
  },
  duration: {
    type: Number,
    default: 600,
  },
  easing: {
    type: String as PropType<TransitionEasing>,
    default: "ease-in-out",
    validator: (value: any) => TransitionEasing.isTransitionEasing(value),
  },
  iconSize: {
    type: String,
    default: "1.9311em",
  },
  /** Specifies custom function on custom defined close event */
  onCloseEvent: {
    type: Function as PropType<(event: DocumentEventMap[keyof DocumentEventMap]) => void>,
  },
  /** Specifies custom function when menu toggled */
  onToggle: { type: Function },
  spacerTopHeight: {
    type: String,
  },
  width: {
    type: [String, Number],
    default: "25%",
    validator: (value: any) => {
      if (typeof value !== "string" && typeof value !== "number") return false;
      if (Number.isNaN(Number.parseFloat(value.toString()))) return false;
      const unit = value
        .toString()
        .replace(/\s/g, "")
        .replace(Number.parseFloat(value.toString()).toString(), "");
      return (
        typeof value === "number" ||
        CssSize.isCssSize(unit) ||
        WidthSize.isWidthSize(unit)
      );
    },
  },
});

const emit = defineEmits<{
  (e: "after-appear", el: HTMLElement): void;
  (e: "after-enter", el: HTMLElement): void;
  (e: "after-leave", el: HTMLElement): void;
  (e: "appear", el: HTMLElement): void;
  (e: "appear-cancelled", el: HTMLElement): void;
  (e: "before-appear", el: HTMLElement): void;
  (e: "before-enter", el: HTMLElement): void;
  (e: "before-leave", el: HTMLElement): void;
  (e: "enter", el: HTMLElement, done: TimerHandler): void;
  (e: "enter-cancelled", el: HTMLElement): void;
  (e: "leave", el: HTMLElement, done: TimerHandler): void;
  (e: "leave-cancelled", el: HTMLElement): void;
  (e: "toggle", burgerMenuOpened: boolean): void;
  (e: "update:active", burgerMenuOpened: boolean): void;
  (e: "update:model-value", burgerMenuOpened: boolean): void;
}>();

const burgerMenuOpened = ref<boolean>(false);
const name = ref<string>("");

const burgerMenuRef = ref<HTMLDivElement | null>(null);

const burgerMenuItems = ref<ComponentInternalInstance[] | null>([]);
provide("burgerMenuItems", burgerMenuItems.value);

/** Specifies two-way binding (for visibility) for the burger menu */
const active = computed({
  get: () => modelValue.value,
  set: (value) => emit("update:model-value", value!),
});

const menuStyle = computed<CSSProperties>(() => {
  return {
    "--spacer-top-height": props.spacerTopHeight
      ? `calc(${props.spacerTopHeight})`
      : `calc(calc(${props.iconSize} + 11px + 11px) - 40px)`,
    "--icon-size": props.iconSize,
  };
});

onMounted(() => {
  props.closeEvents.forEach((closeEvent) => {
    document.addEventListener(closeEvent, handleCLoseEvent);
  });
  if (burgerMenuRef.value) {
    burgerMenuRef.value.addEventListener("click", closeAllContextMenu);
    burgerMenuRef.value.addEventListener("contextmenu", handleContextMenu);
  }
  nextTick(() => {
    name.value = "slide";
    updateMenuDuration();
    updateMenuEasing();
    updateMenuWidth();
  });
});

onUnmounted(() => {
  props.closeEvents.forEach((closeEvent) => {
    document.removeEventListener(closeEvent, handleCLoseEvent);
  });
  if (burgerMenuRef.value) {
    burgerMenuRef.value.removeEventListener("click", closeAllContextMenu);
    burgerMenuRef.value.removeEventListener("contextmenu", handleContextMenu);
  }
});

function beforeAppear(el: Element): void {
  // Emit the event to the parent
  emit("before-appear", el as HTMLElement);
}

function appear(el: Element): void {
  // Emit the event to the parent
  emit("appear", el as HTMLElement);
}

function afterAppear(el: Element): void {
  // Emit the event to the parent
  emit("after-appear", el as HTMLElement);
}

function appearCancelled(el: Element): void {
  // Emit the event to the parent
  emit("appear-cancelled", el as HTMLElement);
}

function beforeEnter(el: Element): void {
  // Emit the event to the parent
  emit("before-enter", el as HTMLElement);
}

function enter(el: Element, done: TimerHandler): void {
  // Emit the event to the parent
  emit("enter", el as HTMLElement, done);
  // Call done() when the transition ends
  // to trigger the @after-enter event.
  setTimeout(done, props.duration);
}

function afterEnter(el: Element): void {
  // Emit the event to the parent
  emit("after-enter", el as HTMLElement);
}

function enterCancelled(el: Element): void {
  // Emit the event to the parent
  emit("enter-cancelled", el as HTMLElement);
}

function beforeLeave(el: Element): void {
  // Emit the event to the parent
  emit("before-leave", el as HTMLElement);
}

function leave(el: Element, done: TimerHandler): void {
  // Emit the event to the parent
  emit("leave", el as HTMLElement, done);
  // Call done() when the transition ends
  // to trigger the @after-leave event.
  // This will also cause v-show
  // to reapply 'display: none'.
  setTimeout(done, props.duration);
}

function afterLeave(el: Element): void {
  // Close all menu items
  closeAllMenuItems();
  // Emit the event to the parent
  emit("after-leave", el as HTMLElement);
}

function leaveCancelled(el: Element): void {
  // Emit the event to the parent
  emit("leave-cancelled", el as HTMLElement);
}

function handleCLoseEvent(event: DocumentEventMap[keyof DocumentEventMap]): void {
  if (props.onCloseEvent) {
    props.onCloseEvent(event);
  } else if (
    burgerMenuRef.value &&
    event.target !== burgerMenuRef.value &&
    !burgerMenuRef.value.contains(event.target as Node)
  ) {
    closeAllContextMenu();
    burgerMenuOpened.value = false;
  }
}

function handleContextMenu(event: MouseEvent): void {
  if (!(event.target as Element)?.closest(".bissantzControls.menuItemControl")) {
    event.preventDefault();
    closeAllContextMenu();
  }
}

const toggle = function () {
  props.onToggle ? props.onToggle() : (burgerMenuOpened.value = !burgerMenuOpened.value);
};

function closeAllContextMenu() {
  if (burgerMenuItems.value) {
    burgerMenuItems.value.forEach((child) => {
      const menuItem = child.proxy as any;
      if (menuItem && menuItem.$.exposed && menuItem.$.exposed.showMenu.value) {
        menuItem.$.exposed.showMenu.value = false;
      }
    });
  }
}

function closeAllMenuItems() {
  if (burgerMenuItems.value) {
    burgerMenuItems.value.forEach((item) => {
      const menuItem = item.proxy as any;
      if (
        menuItem &&
        menuItem.$.exposed &&
        menuItem.$slots.component &&
        menuItem.hideOnClose
      ) {
        menuItem.$.exposed.isOpen.value = false;
      }
    });
  }
}

function updateMenuDuration() {
  if (burgerMenuRef.value) {
    props.duration !== undefined && props.duration !== null
      ? burgerMenuRef.value.style.setProperty(
          "--burger-menu-duration",
          `${props.duration / 1000}s`
        )
      : burgerMenuRef.value.style.setProperty("--burger-menu-duration", `${600 / 1000}s`);
  }
}

function updateMenuEasing() {
  if (burgerMenuRef.value) {
    props.easing !== undefined && props.easing !== null
      ? burgerMenuRef.value.style.setProperty("--burger-menu-easing", props.easing)
      : burgerMenuRef.value.style.setProperty("--burger-menu-easing", "ease-in-out");
  }
}

function updateMenuWidth() {
  if (burgerMenuRef.value) {
    props.width !== undefined && props.width !== null
      ? burgerMenuRef.value.style.setProperty(
          "--burger-menu-width",
          typeof props.width === "number"
            ? `${props.width}px`
            : props.width.replace(/\s/g, "")
        )
      : burgerMenuRef.value.style.setProperty("--burger-menu-width", "25%");
  }
}

watch(
  modelValue,
  () => {
    if (modelValue.value !== undefined && modelValue.value !== burgerMenuOpened.value) {
      burgerMenuOpened.value = modelValue.value;
    }
  },
  { immediate: true }
);

watch(
  burgerMenuOpened,
  () => {
    if (modelValue.value !== undefined && modelValue.value !== burgerMenuOpened.value) {
      modelValue.value = burgerMenuOpened.value;
      emit("toggle", burgerMenuOpened.value);
    }
  },
  { immediate: true }
);

watch(() => props.duration, updateMenuDuration);

watch(() => props.easing, updateMenuEasing);

watch(() => props.width, updateMenuWidth);

defineExpose({
  modelValue,
  active,
  burgerMenuOpened,
  burgerMenuRef,
  name,
  toggle,
});
</script>

<template>
  <div
    class="bissantzControls burgerMenuControl"
    data-pak="bc-controls-d7"
    ref="burgerMenuRef"
    :style="menuStyle"
  >
    <transition
      :name="name"
      @before-appear="beforeAppear"
      @appear="appear"
      @after-appear="afterAppear"
      @appear-cancelled="appearCancelled"
      @before-enter="beforeEnter"
      @enter="enter!"
      @after-enter="afterEnter"
      @enter-cancelled="enterCancelled"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
      @leave-cancelled="leaveCancelled"
    >
      <div class="burgerMenu" v-show="burgerMenuOpened">
        <div class="burgerMenuContent">
          <div class="spacerTop"></div>
          <div class="headerContainer">
            <slot name="header" />
          </div>
          <div class="mainArea">
            <slot name="main-area"></slot>
          </div>
          <div class="spacerBottom">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </transition>
    <div class="menu">
      <div class="menuButton" v-on:click.self="toggle"></div>
    </div>
  </div>
</template>

<style lang="less">
@import "../styles/components/bc-burger-menu.less";
</style>
